import { Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScoreBadge } from "@/components/atomic-crm/scoring/ScoreBadge";

const priorityColors: Record<string, string> = {
  urgent: "bg-red-100 text-red-800",
  high: "bg-orange-100 text-orange-800",
  medium: "bg-blue-100 text-blue-800",
  low: "bg-gray-100 text-gray-600",
};

interface Props {
  task: {
    id: number;
    text: string;
    description?: string;
    priority: string;
    source: string;
    due_date?: string;
    contacts?: {
      first_name: string;
      last_name: string;
      current_score?: number;
      current_tier?: string;
    } | null;
  };
  onDelete?: (taskId: number) => void;
}

export function TaskCard({ task, onDelete }: Props) {
  const isOverdue = task.due_date && new Date(task.due_date) < new Date();

  return (
    <Card className={`${isOverdue ? "border-red-300" : ""}`}>
      <CardContent className="p-3">
        <div className="mb-1 flex items-center justify-between gap-1">
          <div className="flex items-center gap-1">
            <Badge
              className={`text-[10px] ${priorityColors[task.priority] || priorityColors.medium}`}
            >
              {task.priority}
            </Badge>
            {task.source === "system" && (
              <Badge variant="outline" className="text-[10px]">
                auto
              </Badge>
            )}
          </div>
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-muted-foreground hover:text-destructive shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task.id);
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
        <p className="text-sm font-medium">{task.text}</p>
        {task.contacts && (
          <div className="mt-1 flex items-center gap-1">
            <span className="text-xs text-muted-foreground">
              {task.contacts.first_name} {task.contacts.last_name}
            </span>
            <ScoreBadge
              score={task.contacts.current_score}
              tier={task.contacts.current_tier}
            />
          </div>
        )}
        {task.due_date && (
          <p
            className={`mt-1 text-xs ${isOverdue ? "font-medium text-red-600" : "text-muted-foreground"}`}
          >
            Due: {new Date(task.due_date).toLocaleDateString()}
            {isOverdue && " (overdue)"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
